import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { Button } from '@framework/ui/atoms/Button';
import { Input } from '@framework/ui/atoms/Input';
import { useTranslation } from 'react-i18next';
type Props = {
  isOpen: boolean;
  currentName: string;
  onClose(): void;
  onSubmit(newName: string): void;
};

export const ViewTitleEditModal: React.FC<Props> = ({ isOpen, currentName, onClose, onSubmit }: Props) => {
  const { t } = useTranslation();
  const [name, setName] = useState<string>('');
  const [canSave, setCanSave] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setName(currentName);
    }
  }, [currentName, isOpen]);

  useEffect(() => {
    setCanSave(name.trim() !== '' && name.trim() !== currentName);
  }, [name, currentName]);

  const handleSubmit = useCallback(async () => {
    onSubmit(name.trim());
    onClose();
    setName('');
  }, [name, onSubmit, onClose]);

  const handleClose = useCallback(() => {
    setName('');
    onClose();
  }, [onClose]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('canvas.viewTitleEditModal.title')}
      submitButton={
        <Button onClick={handleSubmit} disabled={!canSave}>
          {t('common.action.save')}
        </Button>
      }
      cancelButton={
        <Button onClick={handleClose} color={'secondary'}>
          {t('common.action.cancel')}
        </Button>
      }
    >
      <div className="p-2">
        <Input
          type="text"
          placeholder={t('canvas.viewTitleEditModal.placeholder')}
          value={name}
          onChange={handleChange}
          autoFocus={true}
        />
      </div>
    </GeneralModal>
  );
};
